import { Ref, ref } from "@vue/composition-api";
import { ContentPriceSubject, ContentPriceSubjectTypeEnum } from "@scrile/api-provider/dist/api/ContentPriceProvider";
import {
  InputProfileLinkUpdate,
  ProfileLink,
  ProfileLinkType,
} from "@scrile/api-provider/dist/api/ProfileLinksProvider";
import { PurchaseSubjectTypeEnum } from "@scrile/api-provider/dist/api/PurchaseTypeProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import providers from "@/lib/api";
import { linkFields } from "@/views/PageProfileEdit/const";

export const profileLinkPurchaseSubjectTypeMap: Record<ProfileLinkType, PurchaseSubjectTypeEnum> = {
  [ProfileLinkType.FACEBOOK]: PurchaseSubjectTypeEnum.PROFILE_LINK_FACEBOOK,
  [ProfileLinkType.INSTAGRAM]: PurchaseSubjectTypeEnum.PROFILE_LINK_INSTAGRAM,
  [ProfileLinkType.TWITTER]: PurchaseSubjectTypeEnum.PROFILE_LINK_TWITTER,
  [ProfileLinkType.TIKTOK]: PurchaseSubjectTypeEnum.PROFILE_LINK_TIKTOK,
  [ProfileLinkType.YOUTUBE]: PurchaseSubjectTypeEnum.PROFILE_LINK_YOUTUBE,
  [ProfileLinkType.SNAPCHAT]: PurchaseSubjectTypeEnum.PROFILE_LINK_SNAPCHAT,
  [ProfileLinkType.PATREON]: PurchaseSubjectTypeEnum.PROFILE_LINK_PATREON,
  [ProfileLinkType.ONLYFANS]: PurchaseSubjectTypeEnum.PROFILE_LINK_ONLYFANS,
  [ProfileLinkType.LINKEDIN]: PurchaseSubjectTypeEnum.PROFILE_LINK_LINKEDIN,
  [ProfileLinkType.WHATSAPP]: PurchaseSubjectTypeEnum.PROFILE_LINK_WHATSAPP,
  [ProfileLinkType.TELEGRAM]: PurchaseSubjectTypeEnum.PROFILE_LINK_TELEGRAM,
  [ProfileLinkType.EMAIL]: PurchaseSubjectTypeEnum.PROFILE_LINK_EMAIL,
};

export const profileLinkContentPriceSubjectTypeMap: Record<ProfileLinkType, ContentPriceSubjectTypeEnum> = {
  [ProfileLinkType.FACEBOOK]: ContentPriceSubjectTypeEnum.PROFILE_LINK_FACEBOOK,
  [ProfileLinkType.INSTAGRAM]: ContentPriceSubjectTypeEnum.PROFILE_LINK_INSTAGRAM,
  [ProfileLinkType.TWITTER]: ContentPriceSubjectTypeEnum.PROFILE_LINK_TWITTER,
  [ProfileLinkType.TIKTOK]: ContentPriceSubjectTypeEnum.PROFILE_LINK_TIKTOK,
  [ProfileLinkType.YOUTUBE]: ContentPriceSubjectTypeEnum.PROFILE_LINK_YOUTUBE,
  [ProfileLinkType.SNAPCHAT]: ContentPriceSubjectTypeEnum.PROFILE_LINK_SNAPCHAT,
  [ProfileLinkType.PATREON]: ContentPriceSubjectTypeEnum.PROFILE_LINK_PATREON,
  [ProfileLinkType.ONLYFANS]: ContentPriceSubjectTypeEnum.PROFILE_LINK_ONLYFANS,
  [ProfileLinkType.LINKEDIN]: ContentPriceSubjectTypeEnum.PROFILE_LINK_LINKEDIN,
  [ProfileLinkType.WHATSAPP]: ContentPriceSubjectTypeEnum.PROFILE_LINK_WHATSAPP,
  [ProfileLinkType.TELEGRAM]: ContentPriceSubjectTypeEnum.PROFILE_LINK_TELEGRAM,
  [ProfileLinkType.EMAIL]: ContentPriceSubjectTypeEnum.PROFILE_LINK_EMAIL,
};

export const profileLinkToPurchasePriceSubject = (profileLink: ProfileLink): Subject<PurchaseSubjectTypeEnum> => {
  return {
    subjectId: profileLink.id,
    subjectType: profileLinkPurchaseSubjectTypeMap[profileLink.type],
  };
};

export const profileLinkToContentPriceSubject = (profileLink: ProfileLink): ContentPriceSubject => {
  return {
    subjectId: profileLink.id,
    subjectType: profileLinkContentPriceSubjectTypeMap[profileLink.type],
  };
};

export const getProfileLinkUrl = (profileLink: ProfileLink): string => {
  const prefix = linkFields.find(i => i.name === profileLink.type)?.fieldMeta?.site ?? "";
  return prefix + profileLink.url;
};

const mapToInputProfileLinkUpdate = (
  values: Record<ProfileLinkType, string>,
  userId: string
): InputProfileLinkUpdate[] => {
  return Object.entries(values).map(([type, url]) => ({ type: type as ProfileLinkType, url, userId }));
};

export const profileLinksToMap = (profileLinks: ProfileLink[]): Record<ProfileLinkType, ProfileLink> => {
  return profileLinks.reduce((out, profileLink) => {
    out[profileLink.type] = profileLink;
    return out;
  }, {} as Record<ProfileLinkType, ProfileLink>);
};

export default function useProfileLinks(userId: Ref<string>) {
  const profileLinks = ref<ProfileLink[]>([]);

  const getProfileLinks = async () => {
    profileLinks.value = await providers.ProfileLinksProvider.search({ userId: userId.value });
  };

  const updateProfileLinks = async (values: Record<ProfileLinkType, string>) => {
    const data = mapToInputProfileLinkUpdate(values, userId.value);
    data.forEach(i => {
      i.id = profileLinks.value.find(y => i.type === y.type)?.id;
    });
    await providers.ProfileLinksProvider.update({ data });
  };

  return {
    profileLinks,
    getProfileLinks,
    updateProfileLinks,
  };
}
